import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../../api';
import { FaCheckCircle, FaEnvelope, FaExclamationTriangle, FaArrowLeft, FaSignInAlt } from 'react-icons/fa';
import './VerifyEmailPage.css';

const VerifyEmailPage = () => {
  const { id, hash } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // IMPORTANT: Appeler la route API directement avec l'URL complète
        // car authService.checkEmailVerification n'existe probablement pas
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/verify-email/${id}/${hash}`);
        const data = await response.json();
        
        if (response.ok) {
          setStatus('success');
          setMessage(data.message || 'Votre email a été vérifié avec succès !');
          
          // Stocker le NOUVEAU token renvoyé par le backend
          if (data.token) {
            localStorage.setItem('token', data.token);
            // Mettre à jour l'utilisateur avec le statut vérifié
            const user = data.user || JSON.parse(localStorage.getItem('user') || '{}');
            localStorage.setItem('user', JSON.stringify({
              ...user,
              email_verified_at: new Date().toISOString()
            }));
          }
          
          // Démarrer le compte à rebours pour la redirection
          const interval = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                clearInterval(interval);
                navigate('/dashboard'); // Rediriger vers le dashboard après vérification
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          
          return () => clearInterval(interval);
        } else {
          setStatus('error');
          setMessage(data.error || 'Lien de vérification invalide ou expiré');
        }
      } catch (error) {
        console.error('Erreur vérification:', error);
        setStatus('error');
        setMessage('Erreur de connexion au serveur. Veuillez réessayer plus tard.');
      }
    };

    if (id && hash) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Lien de vérification invalide');
    }
  }, [id, hash, navigate]);

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    
    try {
      // Utiliser la méthode de resend
      await authService.resendVerificationEmail();
      setResendSuccess(true);
      setMessage('✅ Un nouvel email de vérification vous a été envoyé.');
    } catch (error) {
      setMessage('❌ Erreur lors de l\'envoi de l\'email. Veuillez réessayer.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleManualRedirect = () => {
    navigate('/dashboard');
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="verify-email-loading">
            <div className="spinner"></div>
            <h2>Vérification en cours...</h2>
            <p>Nous validons votre adresse email. Veuillez patienter.</p>
          </div>
        );
      
      case 'success':
        return (
          <div className="verify-email-success">
            <FaCheckCircle className="success-icon" />
            <h2>✅ Félicitations !</h2>
            <p className="success-message">{message}</p>
            
            <div className="verification-details">
              <p>Votre compte est maintenant entièrement activé.</p>
              <p>Vous pouvez :</p>
              <ul className="permissions-list">
                <li>Créer et gérer vos annonces</li>
                <li>Participer aux discussions du forum</li>
                <li>Envoyer et recevoir des messages</li>
                <li>Accéder à toutes les fonctionnalités</li>
              </ul>
            </div>
            
            <div className="redirect-info">
              <p>
                <strong>Redirection automatique dans {countdown} seconde{countdown > 1 ? 's' : ''}...</strong>
              </p>
              <button onClick={handleManualRedirect} className="manual-redirect-btn">
                <FaSignInAlt />
                Accéder à mon compte maintenant
              </button>
            </div>
          </div>
        );
      
      case 'error':
        return (
          <div className="verify-email-error">
            <FaExclamationTriangle className="error-icon" />
            <h2>❌ Impossible de vérifier votre email</h2>
            <p className="error-details">{message}</p>
            
            <div className="possible-causes">
              <p><strong>Raisons possibles :</strong></p>
              <ul>
                <li>Le lien a expiré (valable 24 heures)</li>
                <li>Lien déjà utilisé</li>
                <li>Email déjà vérifié</li>
                <li>Problème technique temporaire</li>
              </ul>
            </div>
            
            <div className="resend-section">
              <h3>Besoin d'un nouveau lien ?</h3>
              <button 
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="resend-btn"
              >
                {resendLoading ? (
                  <>
                    <div className="small-spinner"></div>
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <>
                    <FaEnvelope />
                    <span>Renvoyer l'email de vérification</span>
                  </>
                )}
              </button>
              
              {resendSuccess && (
                <div className="resend-success-message">
                  <p>✅ Email envoyé ! Vérifiez votre boîte de réception.</p>
                  <p className="resend-tip">
                    <small>Pensez à vérifier vos spams/courriers indésirables.</small>
                  </p>
                </div>
              )}
            </div>
            
            <div className="alternative-actions">
              <Link to="/connexion" className="login-link">
                <FaSignInAlt />
                Se connecter
              </Link>
              <Link to="/inscription" className="back-link">
                <FaArrowLeft />
                Réessayer l'inscription
              </Link>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="verify-email-container">
      <div className="verify-email-card">
        {renderContent()}
      </div>
    </div>
  );
};

export default VerifyEmailPage;